import React, { useEffect, useState } from 'react';
import { Table } from '@otp/ui';
import { useTranslation } from 'react-i18next';

import {
  SortingDirection,
  StoreFilterInput,
  StoreSortingInput,
  StoresTemplateAssignmentDocument,
  StoresTemplateAssignmentQuery,
  WttpTemplate,
} from 'src/graphql/generated/graphql';
import { getTableConfig } from './config';
import { useQuery } from '@apollo/client';

type StoresTableProps = {
  className?: string;
  filter?: StoreFilterInput;
  template: Pick<WttpTemplate, 'id' | 'name'>;
  onRowsSelected: (rows: string[]) => void;
  selectedRowIds: string[];
};

const PAGINATION_LIMIT = 10;

export const StoresTable = ({
  className,
  filter: filterProp,
  template,
  onRowsSelected,
  selectedRowIds,
}: StoresTableProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<StoreFilterInput>();
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState<StoreSortingInput>({
    name: SortingDirection.Asc,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [sortingField, sortingDirection] = Object.entries(sorting)?.[0];

  const { data: storesData, loading } = useQuery<StoresTemplateAssignmentQuery>(
    StoresTemplateAssignmentDocument,
    {
      variables: {
        filter,
        pagination: {
          limit: PAGINATION_LIMIT,
          offset: page * PAGINATION_LIMIT,
        },
        sorting,
      },
      onCompleted: (data) =>
        setTotalCount(data?.stores?.pagination?.totalCount),
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-only',
    },
  );
  const stores = storesData?.stores?.records || [];
  const tableConfig = getTableConfig(
    { stores, template, selectedRowIds },
    { t },
  );

  useEffect(() => {
    setPage(0);
    // to avoid unnecessary API call
    setFilter(filterProp);
  }, [filterProp]);

  const onColumnSortChange = (
    changedColumn: string,
    direction: 'desc' | 'asc',
  ) => {
    const newSorting = {
      [changedColumn]:
        direction === 'desc' ? SortingDirection.Desc : SortingDirection.Asc,
    };
    setSorting(newSorting);
  };

  return (
    <div className={className}>
      <Table
        data={stores}
        loading={loading}
        columns={tableConfig}
        options={{
          page,
          count: totalCount,
          sort: true,
          onColumnSortChange,
          rowsPerPage: PAGINATION_LIMIT,
          onChangePage: setPage,
          sortOrder: {
            name: sortingField,
            direction:
              sortingDirection === SortingDirection.Desc ? 'desc' : 'asc',
          },
          enableNestedDataAccess: '.',
        }}
        selectedRowIds={selectedRowIds}
        selectableRows="multiple"
        onRowsSelected={onRowsSelected}
      />
    </div>
  );
};
